<template>
    <div class="related-link-file-upload">
        <div class="related-link-file-upload__drop-area"
             :class="{ 'is-dragging': isDragging }"
             @dragenter.prevent="isDragging = true"
             @dragover.prevent="isDragging = true"
             @dragleave.prevent="isDragging = false"
             @drop.prevent="onDrop">
            <input ref="fileInput"
                   type="file"
                   :accept="fileTypesAllowed"
                   :disabled="isUploading"
                   @change="onFileInput">

            <div v-if="!selectedFile">
                Drag a file here or choose a file.
            </div>

            <div v-else>
                <strong>{{ uploadFileName }}</strong>
                <span class="d-block">
                    {{ formattedFileSize }}
                </span>
            </div>
        </div>

        <div v-if="checkingFile" class="mt-2">
            Checking file...
        </div>

        <div v-if="fileExists" class="alert alert-warning mt-2">
            <strong>A file named {{ uploadFileName }} already exists in this folder.</strong>

            <div v-if="loadingReferences" class="mt-2">
                Checking where this file is used...
            </div>

            <template v-else-if="existingFileAsset">
                <div v-if="fileReferences.length" class="mt-2">
                    Replacing this file will update it everywhere it is currently used:

                    <ul class="mb-2 mt-2">
                        <li v-for="reference in referenceLocations"
                            :key="`${reference.PageId}-${reference.SectionId}`">
                            {{ getReferenceLabel(reference) }}
                        </li>
                    </ul>
                </div>

                <div v-else class="mt-2">
                    This managed file is not currently referenced by any Related Links.
                </div>

                <div v-if="!replaceConfirmed" class="mt-2">
                    <button type="button"
                            class="btn btn-warning"
                            @click="confirmReplace">
                        Replace existing file
                    </button>

                    <button type="button"
                            class="btn btn-link"
                            @click="useExistingUrl">
                        Use existing file
                    </button>

                    <button type="button"
                            class="btn btn-link"
                            @click="clear">
                        Cancel
                    </button>
                </div>

                <div v-else class="mt-2">
                    <strong>The existing file will be replaced. This could take up to 45 minutes to update on your browser.</strong>
                </div>
            </template>

            <div v-else-if="!loadingReferences" class="mt-2">
                <div>
                    This file already exists, but its usage cannot be determined.
                    Replacing it may update the document anywhere this URL is currently being used.
                </div>

                <div v-if="!replaceConfirmed" class="mt-2">
                    <button type="button"
                            class="btn btn-warning"
                            @click="confirmReplace">
                        Replace existing file
                    </button>

                    <button type="button"
                            class="btn btn-link"
                            @click="useExistingUrl">
                        Use existing file
                    </button>

                    <button type="button"
                            class="btn btn-link"
                            @click="clear">
                        Cancel
                    </button>
                </div>

                <div v-else class="mt-2">
                    <strong>
                        The existing file will be replaced. This could take up to 45 minutes to update on your browser.
                    </strong>
                </div>
            </div>
        </div>

        <div v-if="errorMessage" class="alert alert-danger mt-2">
            {{ errorMessage }}
        </div>

        <div v-if="isUploading" class="mt-2">
            <div class="progress">
                <div class="progress-bar"
                     role="progressbar"
                     :style="{ width: `${progress}%` }"
                     :aria-valuenow="progress"
                     aria-valuemin="0"
                     aria-valuemax="100">
                    {{ progress }}%
                </div>
            </div>

            <button type="button" class="btn btn-link" @click="cancelUpload">
                Cancel upload
            </button>
        </div>

        <div v-if="selectedFile && !isUploading" class="mt-2">
            <button type="button" class="btn btn-link" @click="clear">
                Remove file
            </button>
        </div>
    </div>
</template>

<script setup>
    import {
        computed,
        ref
    } from 'vue';

    import {
        getAntiForgeryToken,
        requestJson
    } from './helpers';

    const props = defineProps({
        putBlockPath: {
            type: String,
            default: '/Azure/PutBlobBlock'
        },

        commitPath: {
            type: String,
            default: '/Azure/CommitBlobs'
        },

        blobExistsPath: {
            type: String,
            default: '/Azure/BlobExists'
        },

        cdnPath: {
            type: String,
            default: ''
        },

        containerName: {
            type: String,
            default: 'pdfs',
            required: true
        },

        folderPath: {
            type: String,
            default: ''
        },

        fileTypesAllowed: {
            type: String,
            default: '.pdf'
        },

        fileChunkSize: {
            type: Number,
            default: 1024
        },

        maximumFileSizeAllowed: {
            type: Number,
            default: 0
        },

        allowOverwrite: {
            type: Boolean,
            default: false
        },

        fileAssetPath: {
            type: String,
            default: '/RelatedLinks/files'
        },

        currentFile: {
            type: Object,
            default: null
        }
    });

    const emit = defineEmits([
        'file-selected',
        'uploaded',
        'error',
        'cleared',
        'use-existing-url'
    ]);

    const fileInput = ref(null);
    const selectedFile = ref(null);
    const uploadFileName = ref('');
    const fileExists = ref(false);
    const checkingFile = ref(false);
    const isDragging = ref(false);
    const isUploading = ref(false);
    const progress = ref(0);
    const errorMessage = ref('');
    const abortController = ref(null);

    const existingFileAsset = ref(null);
    const fileReferences = ref([]);
    const loadingReferences = ref(false);
    const replaceConfirmed = ref(false);
    const replacementTarget = ref(null);

    const activeContainerName = computed(() => {
        return replaceConfirmed.value && replacementTarget.value
            ? replacementTarget.value.containerName
            : props.containerName;
    });

    const activeFolderPath = computed(() => {
        return replaceConfirmed.value && replacementTarget.value
            ? replacementTarget.value.folderPath
            : normalizedFolderPath.value;
    });

    const normalizedFolderPath = computed(() => {
        if (!props.folderPath) {
            return '';
        }

        let path = props.folderPath;

        if (!path.startsWith('/')) {
            path = `/${path}`;
        }

        if (!path.endsWith('/')) {
            path += '/';
        }

        return path;
    });

    const normalizedCdnPath = computed(() => {
        if (!props.cdnPath) {
            return '';
        }

        return props.cdnPath.endsWith('/')
            ? props.cdnPath
            : `${props.cdnPath}/`;
    });

    const blobName = computed(() => {
        return `${activeFolderPath.value}${uploadFileName.value}`
            .replace(/^\/+/, '');
    });

    const formattedFileSize = computed(() => {
        if (!selectedFile.value) {
            return '';
        }

        return formatBytes(selectedFile.value.size);
    });

    const referenceLocations = computed(() => {
        const locations = [];
        const seen = new Set();

        fileReferences.value.forEach(reference => {
            const key = `${reference.PageId}|${reference.SectionId || ''}`;

            if (!seen.has(key)) {
                seen.add(key);
                locations.push(reference);
            }
        });

        return locations;
    });

    function getCdnTarget(url) {
        if (!url) {
            return null;
        }

        try {
            const parsed = new URL(url);

            if (parsed.protocol !== 'https:' ||
                parsed.hostname.toLowerCase() !== 'cdn.portofportland.com') {
                return null;
            }

            const parts = parsed.pathname
                .split('/')
                .filter(Boolean)
                .map(decodeURIComponent);

            if (parts.length < 2) {
                return null;
            }

            const containerName = parts.shift();
            const fileName = parts.pop();
            const folderPath = parts.length
                ? `/${parts.join('/')}/`
                : '';

            return {
                containerName,
                folderPath,
                fileName,
                url: parsed.toString()
            };
        } catch {
            return null;
        }
    }

    function useExistingUrl() {
        const url = existingFileAsset.value?.Url || buildFallbackUrl();

        emit('use-existing-url', {
            FileName: uploadFileName.value,
            Url: url
        });
    }

    function getReferenceLabel(reference) {
        return reference.SectionId
            ? `${reference.PageId} – ${reference.SectionId}`
            : reference.PageId;
    }

    function confirmReplace() {
        replaceConfirmed.value = true;
    }

    function onFileInput(event) {
        const file = event.target.files?.[0];

        if (file) {
            selectFile(file);
        }
    }

    function onDrop(event) {
        isDragging.value = false;

        const file = event.dataTransfer.files?.[0];

        if (file) {
            selectFile(file);
        }
    }

    async function selectFile(file) {
        clearState();

        // exclude videos
        if (file.type?.startsWith('video/')) {
            setError('Video files are not allowed.');
            return;
        }

        if (props.maximumFileSizeAllowed > 0 && file.size > props.maximumFileSizeAllowed) {
            setError(`The selected file exceeds the maximum allowed size of ${formatBytes(props.maximumFileSizeAllowed)}.`);
            return;
        }

        selectedFile.value = file;
        uploadFileName.value = file.name;

        try {
            const currentTarget = getCdnTarget(props.currentFile?.Url);

            if (currentTarget &&
                currentTarget.fileName.toLowerCase() === file.name.toLowerCase()) {
                fileExists.value = true;
                existingFileAsset.value = props.currentFile;
                replacementTarget.value = currentTarget;

                if (existingFileAsset.value?.Id) {
                    await loadFileReferences(existingFileAsset.value.Id);
                }
            } else {
                const result = await checkIfFileExists(file.name);

                uploadFileName.value = result?.name || file.name;
                fileExists.value = result?.exists === true;

                if (fileExists.value) {
                    await loadExistingFile();
                }
            }

            emit('file-selected', {
                FileName: uploadFileName.value,
                ContentType: file.type || '',
                Size: file.size,
                Exists: fileExists.value
            });
        } catch (error) {
            setError(error);
        }
    }

    async function loadFileReferences(fileAssetId) {
        loadingReferences.value = true;

        try {
            const response = await fetch(`${props.fileAssetPath}/${fileAssetId}/references`, {
                method: 'GET',
                credentials: 'same-origin'
            });

            if (!response.ok) {
                throw new Error(`Unable to determine where the file is being used. (${response.status})`);
            }

            fileReferences.value = await response.json();
        } finally {
            loadingReferences.value = false;
        }
    }

    async function checkIfFileExists(fileName) {
        checkingFile.value = true;

        try {
            const body = new URLSearchParams();

            body.append('container', props.containerName);
            body.append('path', normalizedFolderPath.value);
            body.append('name', fileName);

            const response = await fetch(props.blobExistsPath, {
                method: 'POST',
                credentials: 'same-origin',
                headers: getFormHeaders(),
                body
            });

            if (!response.ok) {
                throw new Error(
                    `Unable to check whether the file exists. (${response.status})`
                );
            }

            return await response.json();
        } finally {
            checkingFile.value = false;
        }
    }

    async function loadExistingFile() {
        loadingReferences.value = true;

        try {
            const assetResponse = await fetch(`${props.fileAssetPath}/${encodeURIComponent(blobName.value)}`, {
                method: 'GET',
                credentials: 'same-origin'
            });

            if (assetResponse.status === 404) {
                existingFileAsset.value = null;
                fileReferences.value = [];
                return;
            }

            if (!assetResponse.ok) {
                throw new Error(`Unable to look up the existing file. (${assetResponse.status})`);
            }

            existingFileAsset.value = await assetResponse.json();
        } finally {
            loadingReferences.value = false;
        }

        await loadFileReferences(existingFileAsset.value.Id);
    }

    async function upload() {
        if (!selectedFile.value) {
            throw new Error('No file has been selected.');
        }

        if (fileExists.value && !replaceConfirmed.value) {
            throw new Error(
                `A file named "${uploadFileName.value}" already exists and replacement has not been confirmed.`
            );
        }

        errorMessage.value = '';
        isUploading.value = true;
        progress.value = 0;
        abortController.value = new AbortController();

        try {
            const file = selectedFile.value;
            const blockIds = [];

            let offset = 0;
            let index = 0;

            while (offset < file.size) {
                const start = offset;
                const end = Math.min(
                    offset + props.fileChunkSize,
                    file.size
                );

                const chunk = file.slice(start, end);

                await uploadBlock(
                    chunk,
                    index,
                    file
                );

                blockIds.push(index.toString());

                offset = end;
                index++;

                progress.value = Math.round(
                    (end / file.size) * 100
                );
            }

            const commitResult = await commitBlocks(
                blockIds,
                file
            );

            progress.value = 100;

            const url =
                commitResult?.CdnPath ||
                buildFallbackUrl();

            const result = {
                BlobName: blobName.value,
                FileName: uploadFileName.value,
                Url: url,
                ContentType: file.type || '',
                Size: file.size,
                IsReplacement: replaceConfirmed.value,
                FileAssetId: existingFileAsset.value?.Id || null
            };

            console.log("replaceConfirmed = " + replaceConfirmed.value + " for path", url);

            if (replaceConfirmed.value) {
                try {
                    await purgeCdn(url);
                } catch (error) {
                    console.warn('File was replaced, but CDN purge failed.', error);
                }
            }

            emit('uploaded', result);

            return result;
        } catch (error) {
            if (error?.name !== 'AbortError') {
                setError(error);
            }

            throw error;
        } finally {
            isUploading.value = false;
            abortController.value = null;
        }
    }

    async function purgeCdn(url) {
        const path = new URL(url).pathname;

        await requestJson(`/Azure/Purge?path=${encodeURIComponent(path)}`, {
            method: 'POST'
        });
    }

    async function uploadBlock(
        chunk,
        index,
        file
    ) {
        const formData = new FormData();

        formData.append('container', activeContainerName.value);
        formData.append('path', activeFolderPath.value);
        formData.append('name', uploadFileName.value);
        formData.append('type', file.type || '');
        formData.append('index', index.toString());
        formData.append('file', chunk, `${uploadFileName.value}-${index}`);
        formData.append('bustCache', "true");

        const response = await fetch(
            props.putBlockPath,
            {
                method: 'POST',
                credentials: 'same-origin',
                headers: getAntiForgeryHeaders(),
                body: formData,
                signal: abortController.value.signal
            }
        );

        if (!response.ok) {
            throw new Error(
                `Error uploading block ${index + 1} for ${uploadFileName.value}.`
            );
        }
    }

    async function commitBlocks(
        blockIds,
        file
    ) {
        const body = new URLSearchParams();

        body.append('container', activeContainerName.value);
        body.append('path', activeFolderPath.value);
        body.append('name', uploadFileName.value);
        body.append('type', file.type || '');

        blockIds.forEach(id => {
            body.append('list', id);
        });

        const response = await fetch(
            props.commitPath,
            {
                method: 'POST',
                credentials: 'same-origin',
                headers: getFormHeaders(),
                body,
                signal: abortController.value.signal
            }
        );

        if (!response.ok) {
            throw new Error(
                `Error committing blocks for ${uploadFileName.value}.`
            );
        }

        return await readResponse(response);
    }

    function buildFallbackUrl() {
        const container = activeContainerName.value
            ? `${activeContainerName.value}/`
            : '';

        const folder = activeFolderPath.value.replace(/^\/+/, '');

        return `${normalizedCdnPath.value}${container}${folder}${uploadFileName.value}`;
    }

    function cancelUpload() {
        abortController.value?.abort();

        isUploading.value = false;
        progress.value = 0;
    }

    function clear() {
        if (isUploading.value) {
            cancelUpload();
        }

        clearState();

        if (fileInput.value) {
            fileInput.value.value = '';
        }

        emit('cleared');
    }

    function clearState() {
        selectedFile.value = null;
        uploadFileName.value = '';
        fileExists.value = false;
        checkingFile.value = false;
        progress.value = 0;
        errorMessage.value = '';

        existingFileAsset.value = null;
        fileReferences.value = [];
        loadingReferences.value = false;
        replaceConfirmed.value = false;
    }

    function setError(error) {
        errorMessage.value =
            error?.message ||
            error?.toString() ||
            'Unable to upload the file.';

        emit('error', errorMessage.value);
    }

    function getAntiForgeryHeaders() {
        const token = getAntiForgeryToken();

        return token
            ? {
                RequestVerificationToken: token
            }
            : {};
    }

    function getFormHeaders() {
        return {
            'Content-Type':
                'application/x-www-form-urlencoded; charset=UTF-8',
            ...getAntiForgeryHeaders()
        };
    }

    async function readResponse(response) {
        const text = await response.text();

        if (!text) {
            return null;
        }

        try {
            return JSON.parse(text);
        } catch {
            return text;
        }
    }

    function formatBytes(bytes) {
        if (!bytes) {
            return '0 bytes';
        }

        const units = [
            'bytes',
            'KB',
            'MB',
            'GB'
        ];

        let value = bytes;
        let unit = 0;

        while (
            value >= 1024 &&
            unit < units.length - 1
        ) {
            value /= 1024;
            unit++;
        }

        return `${value.toFixed(
            unit === 0 ? 0 : 1
        )} ${units[unit]}`;
    }

    defineExpose({
        upload,
        clear
    });
</script>